import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Select,
  Grid,
  useToast,
  useColorModeValue,
  IconButton,
  Spinner,
  Center,
  Badge
} from '@chakra-ui/react';
import { FiHeart, FiShare2, FiPlay } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { useShorts } from '../contexts/UnifiedContentContext';
import { GENRES } from '../constants/genres';
import IconWrapper from '../components/IconWrapper';
import CachedImage from '../components/CachedImage';
import AdSenseSlot from '../components/AdSenseSlot';
import { ADSENSE_CLIENT_ID, ADSENSE_SLOTS } from '../constants/adsense';

const Shorts: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const {
    shorts,
    loading,
    error,
    selectedGenre,
    userLikes,
    likeCounts,
    likingShorts,
    loadShorts,
    setSelectedGenre,
    handleLike,
    handleShare
  } = useShorts();

  // Color mode values
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'white');
  const titleColor = useColorModeValue('gray.900', 'white');
  const labelColor = useColorModeValue('gray.600', 'gray.400');

  const genres = [
    { id: 'all', name: 'All Genres' },
    ...GENRES.map(genre => ({ id: genre.toLowerCase(), name: genre }))
  ];

  useEffect(() => {
    loadShorts(user?.uid);
  }, [selectedGenre, user?.uid, loadShorts]);


  const handleGenreChange = (genre: string) => {
    setSelectedGenre(genre);
  };

  const handleShortClick = (shortId: string) => {
    navigate(`/short/${shortId}`);
  };

  const handleLikeClick = async (shortId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!user) {
      toast({
        title: 'Sign in required',
        description: 'Please sign in to like content',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      await handleLike(shortId, user.uid);
      const currentLike = userLikes[shortId];
      const newLikeType = currentLike === 'like' ? 'dislike' : 'like';
      
      toast({
        title: currentLike === 'like' ? 'Like removed' : 'Liked!',
        description: currentLike === 'like' ? 'You removed your like' : `You ${newLikeType}d this short`,
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update like. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleShareClick = async (shortId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    try {
      await handleShare(shortId, user?.uid);
      toast({
        title: 'Link copied!',
        description: 'Share link copied to clipboard',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to share. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (loading) {
    return (
      <Center h="50vh">
        <Spinner size="xl" color="purple.500" />
      </Center>
    );
  }

  if (error || shorts.length === 0) {
    return (
      <Center h="50vh">
        <VStack gap={6} textAlign="center" maxW="400px">
          <Text fontSize="2xl" fontWeight="bold" color={titleColor}>
            Content Not Found
          </Text>
          <Text fontSize="md" color={labelColor}>
            Content not found or no longer available
          </Text>
          <VStack gap={3}>
            <Button onClick={() => loadShorts(user?.uid)} colorScheme="purple" size="lg">
              Refresh
            </Button>
            <Button 
              onClick={() => navigate('/upload')} 
              variant="outline" 
              colorScheme="purple"
            >
              Upload Content
            </Button>
          </VStack>
        </VStack>
      </Center>
    );
  }

  return (
    <Box bg={bgColor} w="100%" pt={0} mt={0} mb={0} pb={0}>
      <VStack gap={6} maxW="1400px" mx="auto" w="100%" pt={0} mt={0}>
        <AdSenseSlot
          client={ADSENSE_CLIENT_ID}
          slot={ADSENSE_SLOTS.CONTENT_LIST_BANNER}
          format="auto"
          fullWidthResponsive
          style={{ margin: '8px 0' }}
        />
        {/* Header */}
        <HStack w="full" justify="space-between">
          <Text fontSize="2xl" fontWeight="bold" color={titleColor}>
            Shorts
          </Text>
          <Select
            value={selectedGenre}
            onChange={(e) => handleGenreChange(e.target.value)}
            w="200px"
            bg={cardBg}
          >
            {genres.map((genre) => (
              <option key={genre.id} value={genre.id}>
                {genre.name}
              </option>
            ))}
          </Select>
        </HStack>

        {/* Shorts Grid */}
        <Grid
          templateColumns={{
            base: 'repeat(1, 1fr)',
            md: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
            xl: 'repeat(4, 1fr)'
          }}
          gap={6}
          w="full"
        >
          {shorts.map((short) => (
            <Box
              key={short.id}
              bg={cardBg}
              borderRadius="lg"
              overflow="hidden"
              cursor="pointer"
              onClick={() => handleShortClick(short.id)}
              _hover={{ transform: 'translateY(-4px)', shadow: 'lg' }}
              transition="all 0.2s"
            >
              {/* Thumbnail/Poster */}
              <Box position="relative" h="200px">
                <CachedImage
                  src={short.poster || short.thumbnail}
                  alt={short.title}
                  w="100%"
                  h="100%"
                  objectFit="cover"
                />
                <Badge
                  position="absolute"
                  top={2}
                  right={2}
                  colorScheme="red"
                  variant="solid"
                >
                  {short.rating}
                </Badge>
              </Box>

              {/* Content */}
              <VStack p={4} align="start" gap={3}>
                <VStack align="start" gap={1} w="full">
                  <Text
                    fontSize="md"
                    fontWeight="bold"
                    color={textColor}
                    noOfLines={2}
                  >
                    {short.title}
                  </Text>
                  <Text fontSize="sm" color="gray.500" noOfLines={2}>
                    {short.description}
                  </Text>
                </VStack>

                {/* Action Buttons */}
                <HStack w="full" justify="space-between">
                  <HStack gap={2}>
                    <IconButton
                      aria-label="Like"
                      icon={<IconWrapper icon={FiHeart} size={16} />}
                      size="sm"
                      variant={userLikes[short.id] === 'like' ? 'solid' : 'ghost'}
                      colorScheme="red"
                      onClick={(e) => handleLikeClick(short.id, e)}
                      isDisabled={!user || likingShorts.has(short.id)}
                      isLoading={likingShorts.has(short.id)}
                    />
                    <Text fontSize="xs" color="white">
                      {likeCounts[short.id]?.likes || short.likes}
                    </Text>
                  </HStack>

                  <IconButton
                    aria-label="Share"
                    icon={<IconWrapper icon={FiShare2} size={16} />}
                    size="sm"
                    variant="ghost"
                    colorScheme="blue"
                    onClick={(e) => handleShareClick(short.id, e)}
                  />

                  <Button
                    size="sm"
                    colorScheme="purple"
                    leftIcon={<IconWrapper icon={FiPlay} size={16} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShortClick(short.id);
                    }}
                  >
                    Watch
                  </Button>
                </HStack>
              </VStack>
            </Box>
          ))}
        </Grid>

        {shorts.length === 0 && (
          <Center h="200px">
            <VStack gap={4}>
              <Text fontSize="lg" color="gray.500">
                No shorts found
              </Text>
              <Button onClick={() => loadShorts(user?.uid)} colorScheme="purple">
                Refresh
              </Button>
            </VStack>
          </Center>
        )}
      </VStack>
    </Box>
  );
};

export default Shorts;
